import React, { useEffect, useCallback, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import useForm from 'react-hook-form/dist/react-hook-form.ie11';
import styled from '@emotion/styled';
import { useSelector } from 'react-redux';
import { Box, Input, Flex, Text, Image } from 'roo-ui/components';
import { pointsClub, pointsClubPlus, qantasQff } from 'roo-ui/logos';
import { StepForm } from 'components/StepWizard';
import { QFF_OR_EMPTY_REGEX, ABN_OR_EMPTY_REGEX } from 'lib/enums/checkout';
import { getQffAuth } from 'lib/qffAuth';
import { FieldLabel, FieldError } from 'components/Form';
import TextButton from 'components/TextButton';
import { getIsAuthenticated, getMemberId, getPointsClubLevel } from 'store/user/userSelectors';
import { getQuote } from 'store/quote/quoteSelectors';
import ABNInformation from './ABNInformation';
import QFFInformation from './QFFInformation';
import { mediaQuery } from 'lib/styledSystem';
import { POINTS_CLUB_PLUS } from 'lib/enums/pointsClub';
import { themeGet } from 'styled-system';
import { useDataLayer } from 'hooks/useDataLayer';
import PointsClubModal from 'components/PointsClubModal';
import PointsEarnDisplay from 'components/PointsEarnDisplay';
import useCheckoutPointsEarned from 'pages/Checkout/hooks/useCheckoutPointsEarned';

const login = async () => {
  const qffAuth = await getQffAuth();
  qffAuth.showLoginModal();
};

const TotalPointsContainer = styled(Flex)`
  padding-bottom: ${themeGet('space.4')};
  border-bottom: ${themeGet('borders.1')} ${themeGet('colors.greys.alto')};
  text-align: center;
  color: ${themeGet('colors.greys.charcoal')};
  flex-direction: column;
  border-radius: ${themeGet('radii.default')};
  ${mediaQuery.minWidth.md} {
    flex-direction: row;
    margin-bottom: ${themeGet('space.7')};
    padding: ${themeGet('space.3')} ${themeGet('space.5')};
    border: ${themeGet('borders.1')} ${themeGet('colors.greys.alto')};
  }
`;

const PointsClubImage = styled(Image)`
  margin: 0 0 ${themeGet('space.4')} ${themeGet('space.4')};
  padding-left: ${themeGet('space.4')};
  border-left: 1px solid ${themeGet('colors.greys.alto')};
  width: 125px;
  height: 54px;

  ${mediaQuery.minWidth.md} {
    margin-bottom: 0;
    width: 0;
  }
`;

const QantasPointsForm = ({ step }) => {
  const { register, errors, handleSubmit } = useForm({ mode: 'onBlur' });
  const { formData, updateFormData } = step;
  const isAuthenticated = useSelector(getIsAuthenticated);
  const qffNumber = useSelector(getMemberId);
  const { offer } = useSelector(getQuote);
  const { pointsEarned } = useCheckoutPointsEarned({ pointsEarned: offer.pointsEarned });
  const pointsClubLevel = useSelector(getPointsClubLevel);
  const [isQffInputDirty, setQffInputDirty] = useState(false);
  const isPointsClubPlus = pointsClubLevel === POINTS_CLUB_PLUS;

  const { emitInteractionEvent } = useDataLayer();
  const completeStep = useCallback(
    (data) => {
      step.completeStep(data);
      emitInteractionEvent({ type: 'Continue Button', value: 'Earn Qantas Points Button Selected' });
      if (data.qffNumber) {
        emitInteractionEvent({ type: 'Frequent Flyer Number', value: 'Number Added' });
      }
      if (data.abn) {
        emitInteractionEvent({ type: 'Business Rewards', value: 'ABN Added' });
      }
    },
    [step, emitInteractionEvent],
  );

  const qffNumberChanged = useCallback(
    (event) => {
      if (pointsClubLevel) {
        setQffInputDirty(event.target.value !== qffNumber);
      }
    },
    [qffNumber, pointsClubLevel],
  );

  const showLoginModal = useCallback(async () => {
    emitInteractionEvent({ type: 'Points Club Login Now Text', value: 'Login Now Link Selected' });
    await login();
  }, [emitInteractionEvent]);

  useEffect(() => {
    if (isAuthenticated && !formData.qffNumber) {
      updateFormData({ qffNumber });
    }
  }, [qffNumber, formData, isAuthenticated, updateFormData]);

  return (
    <StepForm {...step} completeStep={completeStep} handleSubmit={handleSubmit}>
      <Box mb={[4, 8]}>
        <TotalPointsContainer data-testid="qff-and-abn-points" mx={[1, 0]}>
          <Flex flexWrap={['wrap', 'nowrap']} justifyContent={['center', 'normal']} alignItems="center">
            <Image src={qantasQff} alt="Qantas Frequent Flyer Logo" mb={[4, 0]} height={['32px', 'initial']} width={['125px', 'initial']} />
            {pointsClubLevel && <PointsClubImage src={isPointsClubPlus ? pointsClubPlus : pointsClub} alt="Points Club Logo" />}
            <Text fontSize={['sm', 'base']} textAlign={['left', 'center']} ml={[0, 9]}>
              You&apos;ll earn{' '}
              <PointsEarnDisplay fontSize={['sm', 'base']} total={pointsEarned.qffPoints.total} data-testid="total-qff-points" />
              {` Qantas Points on your booking`}
              {pointsClubLevel && (
                <Fragment>
                  {` as a `}
                  <PointsClubModal>
                    <Text fontSize={['sm', 'base']} color="greys.charcoal" textDecoration="underline" data-testid="points-club-modal-link">
                      Points Club
                    </Text>
                  </PointsClubModal>
                  {` member,`}
                </Fragment>
              )}
              {` plus `}
              <PointsEarnDisplay fontSize={['sm', 'base']} total={pointsEarned.qbrPoints.total} data-testid="total-qbr-points" />
              {` Qantas Points for your business*.`}
            </Text>
          </Flex>
        </TotalPointsContainer>
        {!isAuthenticated && (
          <Box mt={3}>
            <Text fontSize="sm" color="greys.steel">
              Are you a Points Club member?
            </Text>
            &nbsp;
            <TextButton onClick={showLoginModal} color="red" fontSize="sm" data-testid="points-club-banner-login-cta">
              Login now
            </TextButton>
            &nbsp;
            <Text fontSize="sm" color="greys.steel">
              to access more bonus points on your hotel booking.
            </Text>
          </Box>
        )}
        <Flex flexDirection={['column', 'row']} mt={[4, 7]}>
          <FieldLabel htmlFor="qffNumber" flex="1 1 0" mr={[0, 4]} mb={[5, 3]}>
            Qantas Frequent Flyer number <Text color="greys.dusty">(optional)</Text>
            <Box position="relative">
              <Input
                id="qffNumber"
                name="qffNumber"
                defaultValue={formData.qffNumber}
                ref={register({ pattern: { value: QFF_OR_EMPTY_REGEX, message: 'Please enter a valid Frequent Flyer Number' } })}
                error={!!errors.qffNumber}
                onChange={qffNumberChanged}
                color="greys.steel"
                lineHeight="loose"
              />
            </Box>
            <FieldError error={errors.qffNumber} data-testid="qff-number-error" />
            {isQffInputDirty && (
              <Text color="greys.dusty" display="block" data-testid="qff-points-club-notice">
                Please note, only Qantas Points Club members are eligible for Points Club bonus points.
              </Text>
            )}
            <QFFInformation />
          </FieldLabel>
          <FieldLabel htmlFor="abn" flex="1 1 0" mb={[0, 3]}>
            ABN for business trips <Text color="greys.dusty">(optional)</Text>
            <Box position="relative">
              <Input
                id="abn"
                name="abn"
                defaultValue={formData.abn}
                ref={register({ pattern: { value: ABN_OR_EMPTY_REGEX, message: 'Please enter a valid 11-digit ABN' } })}
                error={!!errors.abn}
                color="greys.steel"
                lineHeight="loose"
              />
            </Box>
            <FieldError error={errors.abn} data-testid="abn-error" />
            <ABNInformation />
          </FieldLabel>
        </Flex>
      </Box>
    </StepForm>
  );
};

QantasPointsForm.propTypes = {
  step: PropTypes.shape({
    formData: PropTypes.object,
    updateFormData: PropTypes.func.isRequired,
    completeStep: PropTypes.func.isRequired,
  }),
};

QantasPointsForm.defaultProps = {
  step: {
    formData: {},
  },
};

export default QantasPointsForm;
